export interface ApiNetworkErrorDetails {
  endpoint: string;
  url: string;
  method: string;
  headerNames: string[];
  crossOrigin: boolean;
  retryAttempts: number;
  cause?: string;
  hint?: string;
}

export class ApiNetworkError extends Error {
  readonly details: ApiNetworkErrorDetails;

  constructor(message: string, details: ApiNetworkErrorDetails) {
    super(message);
    this.name = 'ApiNetworkError';
    this.details = details;
  }
}

function headerNames(headers: HeadersInit | undefined): string[] {
  if (!headers) return [];
  if (headers instanceof Headers) {
    return [...headers.keys()].sort((left, right) => left.localeCompare(right));
  }
  if (Array.isArray(headers)) {
    return headers.map(([name]) => name).sort((left, right) => left.localeCompare(right));
  }
  return Object.keys(headers).sort((left, right) => left.localeCompare(right));
}

function isCrossOrigin(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

export function buildNetworkError(
  endpoint: string,
  url: string,
  requestInit: RequestInit,
  retryAttempts: number,
  cause: unknown,
): ApiNetworkError {
  const names = headerNames(requestInit.headers as HeadersInit | undefined);
  const debugHeaders = names.filter((name) => name.startsWith('x-financials-debug'));
  const crossOrigin = isCrossOrigin(url);
  const causeMessage = cause instanceof Error ? cause.message : String(cause);
  const hint = debugHeaders.length > 0 && crossOrigin
    ? `Possible CORS preflight rejection for custom headers: ${debugHeaders.join(', ')}`
    : undefined;
  let message = `Network request failed for ${(requestInit.method ?? 'GET').toUpperCase()} ${url}`;
  if (causeMessage) message += `: ${causeMessage}`;
  if (hint) message += `. ${hint}`;
  return new ApiNetworkError(message, {
    endpoint, url, method: (requestInit.method ?? 'GET').toUpperCase(),
    headerNames: names, crossOrigin, retryAttempts, cause: causeMessage, hint,
  });
}
